/**
 * ImageGridForm: edit form for the `image-grid` block.
 *
 * data: { images: [{ url, alt, caption? }] }
 *
 * Array editing: each image is an editable row (url / alt / caption) with a
 * Remove control; an Add Image button appends a fresh blank row. Rows are
 * keyed by array index: this is a flat authoring list, not a reorderable
 * one, so index keys are stable enough for editing.
 *
 * Handles a missing / non-array `images` by treating it as an empty list.
 *
 * Round-3 review finding 4: per-row inline warning when a URL fails the
 * image allowlist, so the author sees actionable feedback rather than a
 * generic "Failed to save the draft." after the server's validateBlockUrls
 * returns 400. Warnings are advisory: the save button is NOT disabled,
 * because the server-side validator is the security boundary.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';

import { isAllowedImageUrl } from '../../utils/urlValidator';

export default function ImageGridForm({ data, onChange }) {
    const d = data || {};
    const images = Array.isArray(d.images) ? d.images : [];

    const updateImages = (nextImages) => {
        onChange({ ...d, images: nextImages });
    };

    const updateRow = (index, field, value) => {
        const next = images.map((img, i) =>
            i === index ? { ...img, [field]: value } : img
        );
        updateImages(next);
    };

    const addRow = () => {
        updateImages([...images, { url: '', alt: '', caption: '' }]);
    };

    const removeRow = (index) => {
        updateImages(images.filter((_, i) => i !== index));
    };

    return (
        <>
            {images.length === 0 && (
                <p className="text-muted small mb-3">
                    No images yet. Add one below.
                </p>
            )}
            {images.map((img, index) => {
                const rowUrlInvalid =
                    Boolean(img.url) && !isAllowedImageUrl(img.url);
                return (
                    <Card key={`grid-img-${index}`} className="mb-2">
                        <Card.Body className="p-3">
                            <Row className="g-2 align-items-end">
                                <Col md={4}>
                                    <Form.Label className="small mb-1">
                                        Image URL
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        size="sm"
                                        value={img.url || ''}
                                        onChange={(e) =>
                                            updateRow(
                                                index,
                                                'url',
                                                e.target.value
                                            )
                                        }
                                        placeholder="https://…"
                                        isInvalid={rowUrlInvalid}
                                    />
                                    {rowUrlInvalid && (
                                        <Form.Control.Feedback type="invalid">
                                            URL must be http(s) or a same-site
                                            path. data:, javascript:, and
                                            protocol-relative URLs are rejected
                                            on save.
                                        </Form.Control.Feedback>
                                    )}
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small mb-1">
                                        Alt Text
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        value={img.alt || ''}
                                        onChange={(e) =>
                                            updateRow(
                                                index,
                                                'alt',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Describe the image"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small mb-1">
                                        Caption
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        value={img.caption || ''}
                                        onChange={(e) =>
                                            updateRow(
                                                index,
                                                'caption',
                                                e.target.value
                                            )
                                        }
                                        placeholder="Optional"
                                    />
                                </Col>
                                <Col md={2} className="d-grid">
                                    <Button
                                        type="button"
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeRow(index)}
                                    >
                                        Remove
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                );
            })}
            <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={addRow}
            >
                + Add Image
            </Button>
        </>
    );
}

ImageGridForm.propTypes = {
    data: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};

ImageGridForm.defaultProps = {
    data: {},
};
